import React, { useEffect, useState } from "react";
import {
  Radio, Play, Loader2, CheckCircle2, X, Edit3, Save,
  Activity, RefreshCw, AlertTriangle, Music, Music2, Sparkles, Trash2, Clock,
  Image as ImageIcon, Download,
} from "lucide-react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { ContextualHelp } from "@/components/help/ContextualHelp";
import { PlatformIcon, getPlatformMeta } from "@/components/PlatformIcon";
import { api, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TrackReportsTab } from "@/components/nowplaying/TrackReportsTab";
import { toast } from "sonner";

const PLATFORM_LABEL = {
  meta_facebook: "Facebook",
  x_twitter: "X",
  meta_instagram: "Instagram",
  tiktok: "TikTok",
  pinterest: "Pinterest",
  threads: "Threads",
  bluesky: "Bluesky",
  telegram: "Telegram",
  discord: "Discord",
  youtube_community: "YouTube Community",
  whatsapp_business: "WhatsApp",
};

const STATUS_PILL = {
  pending_approval: { label: "PENDING", cls: "border-brand-amber/40 bg-brand-amber/10 text-brand-amber" },
  approved: { label: "APPROVED", cls: "border-sky-500/40 bg-sky-500/10 text-sky-300" },
  posted: { label: "POSTED", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  failed: { label: "FAILED", cls: "border-brand-onair/40 bg-brand-onair/10 text-brand-onair" },
  pending_keys: { label: "NEEDS KEYS", cls: "border-brand-amber/30 bg-brand-amber/10 text-brand-amber" },
  held: { label: "HELD FOR PEAK", cls: "border-violet-500/40 bg-violet-500/10 text-violet-300" },
  skipped: { label: "SKIPPED", cls: "border-line bg-bg-elevated text-ink-muted" },
};

export default function NowPlaying() {
  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="nowplaying-page">
      <PageHeader
        eyebrow="// NOW PLAYING AUTO-POSTER"
        title="What's on air → on socials."
        description="Read your stream metadata, generate AI captions, post to your channels. Rules-driven, approval-gated, history-logged."
        action={<ContextualHelp featureKey="now-playing" />}
      />
      <ImportedLibraryDraftBanner />

      <Tabs defaultValue="live">
        <TabsList className="bg-bg-surface border border-line p-1 rounded-md mb-6">
          <TabsTrigger value="live"    data-testid="tab-np-live"    className="data-[state=active]:bg-brand-amber data-[state=active]:text-black">Live</TabsTrigger>
          <TabsTrigger value="queue"   data-testid="tab-np-queue"   className="data-[state=active]:bg-brand-amber data-[state=active]:text-black">Approval Queue</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-np-history" className="data-[state=active]:bg-brand-amber data-[state=active]:text-black">History</TabsTrigger>
          <TabsTrigger value="rules"   data-testid="tab-np-rules"   className="data-[state=active]:bg-brand-amber data-[state=active]:text-black">Rules & Setup</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-np-reports" className="data-[state=active]:bg-brand-amber data-[state=active]:text-black">Track Reports</TabsTrigger>
        </TabsList>
        <TabsContent value="live"><LiveTab /></TabsContent>
        <TabsContent value="queue"><QueueTab /></TabsContent>
        <TabsContent value="history"><HistoryTab /></TabsContent>
        <TabsContent value="rules"><RulesTab /></TabsContent>
        <TabsContent value="reports"><TrackReportsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// Surfaces content sent from the Library "Send to → Now Playing draft" action.
function ImportedLibraryDraftBanner() {
  const [imp, setImp] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("bfa:import_nowplaying_draft");
      if (raw) setImp(JSON.parse(raw));
    } catch { /* no-op */ }
  }, []);
  if (!imp) return null;
  const dismiss = () => { sessionStorage.removeItem("bfa:import_nowplaying_draft"); setImp(null); };
  const copy = async () => {
    try { await navigator.clipboard.writeText(imp.caption || ""); toast.success("Caption copied — paste into a draft post"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="mb-6 rounded-xl border border-cyan-500/40 bg-cyan-500/5 p-4" data-testid="nowplaying-imported-banner">
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-lg bg-cyan-500/15 text-cyan-300 grid place-items-center shrink-0">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300">// DRAFT FROM CONTENT LIBRARY</span>
            {imp.platform && (
              <span className="rounded-full bg-cyan-500/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-cyan-200">
                {imp.platform}
              </span>
            )}
          </div>
          <p className="text-xs text-ink-secondary mt-1 mb-2">
            Imported caption — copy and paste into a Now Playing post or rule template.
          </p>
          <div className="rounded-lg border border-line bg-bg p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Caption</span>
              <button onClick={copy} className="text-[11px] text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1" data-testid="nowplaying-imported-copy">
                <Sparkles className="h-3 w-3" /> Copy caption
              </button>
            </div>
            <div className="text-xs text-ink-primary whitespace-pre-wrap line-clamp-5">{imp.caption}</div>
          </div>
        </div>
        <button onClick={dismiss} className="text-ink-muted hover:text-ink-primary transition" data-testid="nowplaying-imported-dismiss">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/* ============== LIVE ============== */
function LiveTab() {
  const [config, setConfig] = useState(null);
  const [track, setTrack] = useState(null);
  const [cardOpen, setCardOpen] = useState(false);   // Update #192 · share-image modal
  const [cardFmt, setCardFmt] = useState("square");
  const [polling, setPolling] = useState(false);
  const [stats, setStats] = useState({});
  const [lastResult, setLastResult] = useState(null);
  const [streamStatus, setStreamStatus] = useState(null);
  const [rechecking, setRechecking] = useState(false);
  const [tiktokSharing, setTiktokSharing] = useState(false);

  const shareToTikTok = async () => {
    const t = track || lastResult?.track;
    setTiktokSharing(true);
    const toastId = toast.loading("Building your TikTok post…");
    try {
      const r = await api.post("/nowplaying/share/tiktok", {
        title: t?.title || t?.raw || "",
        artist: t?.artist || "",
      });
      toast.success(
        r.data.note
          ? `Posted to TikTok ✓ ${r.data.note}`
          : "Posted to TikTok ✓ Check your profile!",
        { id: toastId, duration: 10000 }
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "TikTok share failed", { id: toastId, duration: 10000 });
    } finally { setTiktokSharing(false); }
  };

  const load = async () => {
    const [c, s] = await Promise.all([api.get("/nowplaying/config"), api.get("/nowplaying/stats")]);
    setConfig(c.data);
    setStats(s.data);
  };
  const loadStreamStatus = async () => {
    try {
      const r = await api.get("/nowplaying/stream-status");
      setStreamStatus(r.data);
    } catch { /* non-blocking */ }
  };
  useEffect(() => { load(); loadStreamStatus(); }, []);

  const recheckStream = async () => {
    setRechecking(true);
    try {
      const r = await api.post("/nowplaying/stream-status/check");
      setStreamStatus(r.data);
      if (r.data.status === "online") toast.success("Stream is online" + (r.data.mean_db != null ? " and audible" : ""));
      else if (r.data.status === "silent") toast.error("Stream is up but SILENT — check your encoder audio input");
      else toast.error("Still offline — we'll email you when it recovers");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-check failed");
    } finally { setRechecking(false); }
  };

  const probe = async () => {
    if (!config?.stream_url) { toast.error("Set a stream URL in Rules tab"); return; }
    setPolling(true);
    try {
      const res = await api.get(`/nowplaying/probe?url=${encodeURIComponent(config.stream_url)}`);
      setTrack(res.data);
    } catch { toast.error("Probe failed"); }
    finally { setPolling(false); }
  };

  const runCheck = async () => {
    setPolling(true);
    try {
      const res = await api.post("/nowplaying/check");
      setLastResult(res.data);
      if (res.data.skipped_reason) toast.info(`Skipped: ${res.data.skipped_reason}`);
      else toast.success(`Queued ${res.data.queued.length} post(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Check failed");
    } finally { setPolling(false); }
  };

  return (
    <div className="space-y-6">
      {streamStatus?.status === "offline" && (
        <div className="rounded-xl border border-brand-onair/50 bg-brand-onair/10 p-4" data-testid="np-stream-offline-banner">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-brand-onair/20 text-brand-onair grid place-items-center shrink-0">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-onair font-bold">// STREAM OFFLINE</span>
                <span className="rounded-full border border-brand-onair/40 bg-brand-onair/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-brand-onair">
                  {streamStatus.error || "unreachable"}
                </span>
              </div>
              <p className="text-sm text-ink-primary mt-1.5">
                Your stream URL isn't responding — listeners can't hear you right now. Check your encoder or stream host.
              </p>
              <div className="flex items-center gap-3 mt-2 text-[11px] text-ink-muted font-mono flex-wrap">
                {streamStatus.offline_since && (
                  <span data-testid="np-stream-offline-since">
                    <Clock className="h-3 w-3 inline mr-1 -mt-0.5" />
                    down since {new Date(streamStatus.offline_since).toLocaleString()}
                  </span>
                )}
                {streamStatus.last_checked_at && (
                  <span data-testid="np-stream-last-checked">last checked {new Date(streamStatus.last_checked_at).toLocaleTimeString()}</span>
                )}
                {streamStatus.down_alert_sent_at && (
                  <span className="text-brand-amber" data-testid="np-stream-alert-sent">✓ alert email sent — you'll get another when it's back</span>
                )}
              </div>
            </div>
            <button
              onClick={recheckStream}
              disabled={rechecking}
              data-testid="np-stream-recheck-btn"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-brand-onair/40 bg-bg-elevated px-3 py-1.5 text-xs text-brand-onair hover:bg-brand-onair/15 transition disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${rechecking ? "animate-spin" : ""}`} />
              Re-check now
            </button>
          </div>
        </div>
      )}

      {streamStatus?.status === "silent" && (
        <div className="rounded-xl border border-brand-amber/50 bg-brand-amber/10 p-4" data-testid="np-stream-silent-banner">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-brand-amber/20 text-brand-amber grid place-items-center shrink-0">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber font-bold">// DEAD AIR</span>
                {streamStatus.mean_db != null && (
                  <span className="rounded-full border border-brand-amber/40 bg-brand-amber/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-brand-amber">
                    avg {streamStatus.mean_db} dB
                  </span>
                )}
              </div>
              <p className="text-sm text-ink-primary mt-1.5">
                Your stream is reachable but broadcasting silence — check your playout software or encoder audio input.
              </p>
              <div className="flex items-center gap-3 mt-2 text-[11px] text-ink-muted font-mono flex-wrap">
                {streamStatus.silent_since && (
                  <span data-testid="np-stream-silent-since">
                    <Clock className="h-3 w-3 inline mr-1 -mt-0.5" />
                    silent since {new Date(streamStatus.silent_since).toLocaleString()}
                  </span>
                )}
                {streamStatus.silent_alert_sent_at && (
                  <span className="text-brand-amber">✓ alert email sent — you'll get another when audio returns</span>
                )}
              </div>
            </div>
            <button
              onClick={recheckStream}
              disabled={rechecking}
              data-testid="np-stream-silent-recheck-btn"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-brand-amber/40 bg-bg-elevated px-3 py-1.5 text-xs text-brand-amber hover:bg-brand-amber/15 transition disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${rechecking ? "animate-spin" : ""}`} />
              Re-check now
            </button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-line bg-bg-surface p-6" data-testid="np-current-track">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-onair onair-dot" />
              // CURRENT TRACK
            </div>
            <div className="flex gap-2">
              {(track || lastResult?.track) && (
                <button
                  onClick={shareToTikTok}
                  disabled={tiktokSharing}
                  data-testid="np-share-tiktok-btn"
                  className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs hover:text-pink-400 transition disabled:opacity-50"
                >
                  {tiktokSharing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Music2 className="h-3 w-3" />}
                  {tiktokSharing ? "Posting…" : "Share to TikTok"}
                </button>
              )}
              {(track || lastResult?.track) && (
                <button
                  onClick={() => setCardOpen(true)}
                  data-testid="np-share-image-btn"
                  className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs hover:text-brand-amber transition"
                >
                  <ImageIcon className="h-3 w-3" />
                  Share image
                </button>
              )}
              <button onClick={probe} disabled={polling} data-testid="np-probe-btn" className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs hover:text-cyan-300 transition disabled:opacity-50">
                <RefreshCw className={`h-3 w-3 ${polling ? "animate-spin" : ""}`} />
                Probe
              </button>
              <button onClick={runCheck} disabled={polling || !config?.enabled} data-testid="np-check-btn" className="inline-flex items-center gap-1 rounded-md bg-brand-amber px-3 py-1.5 type-button-label text-black amber-glow disabled:opacity-50">
                <Sparkles className="h-3 w-3" />
                Run auto-poster
              </button>
            </div>
          </div>
          {!track && !lastResult && (
            <div className="py-8 text-center text-ink-muted">
              <Music className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <div className="text-sm">Press <span className="text-brand-amber font-mono">PROBE</span> to read your stream metadata.</div>
            </div>
          )}
          {(track || lastResult?.track) && (
            <div>
              {(() => {
                const t = track || lastResult.track;
                return (
                  <>
                    <div className="flex items-baseline gap-3 mb-1">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{t.provider || "—"}</span>
                      {!t.ok && t.error && <span className="font-mono text-[10px] text-brand-onair">// {t.error}</span>}
                    </div>
                    <div className="font-display text-3xl font-black tracking-tighter leading-tight" data-testid="np-now-title">
                      {t.title || t.raw || "—"}
                    </div>
                    {t.artist && <div className="text-xl text-ink-secondary mt-1" data-testid="np-now-artist">{t.artist}</div>}
                  </>
                );
              })()}
            </div>
          )}
          {lastResult?.skipped_reason && (
            <div className="mt-4 rounded-lg border border-brand-amber/30 bg-brand-amber/5 p-3 text-xs text-ink-secondary" data-testid="np-skip-reason">
              <span className="font-mono text-brand-amber">SKIPPED:</span> {lastResult.skipped_reason}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-3" data-testid="np-status-card">
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// STATUS</div>
          <Row label="Auto-poster" value={config?.enabled ? "ON" : "OFF"} accent={config?.enabled} />
          <Row label="Stream URL" value={config?.stream_url ? "configured" : "not set"} mono />
          {streamStatus?.configured && (
            <div className="flex items-center justify-between" data-testid="np-stream-health-row">
              <span className="text-xs text-ink-muted">Stream health</span>
              <span className={`font-mono text-xs font-bold inline-flex items-center gap-1.5 ${streamStatus.status === "online" ? "text-emerald-400" : "text-brand-onair"}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${streamStatus.status === "online" ? "bg-emerald-400" : "bg-brand-onair onair-dot"}`} />
                {streamStatus.status === "online" ? "ONLINE" : "OFFLINE"}
              </span>
            </div>
          )}
          <Row label="Platforms" value={config?.platforms?.length || 0} />
          <Row label="Frequency" value={config?.frequency?.replace("_", "/") || "every"} />
          <Row label="Songs detected" value={stats?.total_tracks_detected || 0} />
          <Row label="Posted" value={stats?.by_status?.posted || 0} accent />
          <Row label="Pending approval" value={stats?.by_status?.pending_approval || 0} />
        </div>
      </div>

      {/* Update #194 · embeddable Recently Played widget */}
      {config?.station_id && (
        <div className="rounded-xl border border-line bg-bg-surface p-6" data-testid="np-widget-embed-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// RECENTLY PLAYED WIDGET · EMBED ON YOUR WEBSITE</div>
            <a
              href={`/widget/recently-played/${config.station_id}`}
              target="_blank" rel="noreferrer"
              data-testid="np-widget-open"
              className="text-xs text-brand-amber hover:underline"
            >
              Open widget ↗
            </a>
          </div>
          <p className="text-xs text-ink-secondary mb-3">
            Live "last 10 tracks" list with artwork — paste this snippet into any page. Updates itself every minute.
          </p>
          <div className="flex gap-2">
            <textarea
              readOnly
              rows={3}
              data-testid="np-widget-snippet"
              onFocus={(e) => e.target.select()}
              className="flex-1 rounded-md border border-line bg-bg-elevated px-3 py-2 font-mono text-[11px] text-ink-secondary resize-none"
              value={`<iframe src="${window.location.origin}/widget/recently-played/${config.station_id}" width="380" height="620" style="border:0;border-radius:12px;overflow:hidden" title="Recently played on ${window.location.hostname}" loading="lazy"></iframe>`}
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(`<iframe src="${window.location.origin}/widget/recently-played/${config.station_id}" width="380" height="620" style="border:0;border-radius:12px;overflow:hidden" title="Recently played" loading="lazy"></iframe>`);
                toast.success("Embed code copied");
              }}
              data-testid="np-widget-copy"
              className="self-start rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs hover:text-brand-amber transition"
            >
              Copy
            </button>
          </div>
        </div>
      )}

      {/* Update #192 · Now Playing share-image modal */}
      {cardOpen && (() => {
        const t = track || lastResult?.track || {};
        const q = `fmt=${cardFmt}&title=${encodeURIComponent(t.title || t.raw || "")}&artist=${encodeURIComponent(t.artist || "")}`;
        const imgUrl = `${API_BASE}/nowplaying/card.png?${q}`;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="np-card-modal" onClick={() => setCardOpen(false)}>
            <div className="rounded-xl border border-line bg-bg-surface p-4 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// SHARE IMAGE</div>
                <button onClick={() => setCardOpen(false)} data-testid="np-card-close" className="text-ink-muted hover:text-ink-primary"><X className="h-4 w-4" /></button>
              </div>
              <div className="flex gap-2 mb-3">
                {["square", "story"].map((f) => (
                  <button key={f} onClick={() => setCardFmt(f)} data-testid={`np-card-fmt-${f}`}
                    className={`rounded-md px-3 py-1.5 text-xs border transition ${cardFmt === f ? "border-brand-amber text-brand-amber bg-brand-amber/10" : "border-line text-ink-muted hover:text-ink-primary"}`}>
                    {f === "square" ? "Square · 1080×1080" : "Story · 1080×1920"}
                  </button>
                ))}
              </div>
              <div className="rounded-lg overflow-hidden border border-line bg-black flex items-center justify-center" style={{ maxHeight: "55vh" }}>
                <img src={imgUrl} alt="Now playing card" data-testid="np-card-preview" className="max-w-full object-contain" style={{ maxHeight: "55vh" }} />
              </div>
              <div className="flex justify-end mt-3">
                <a href={`${imgUrl}&download=1`} data-testid="np-card-download"
                  className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black amber-glow">
                  <Download className="h-3.5 w-3.5" /> Download PNG
                </a>
              </div>
            </div>
          </div>
        );
      })()}

      {lastResult?.queued?.length > 0 && (
        <div className="rounded-xl border border-line bg-bg-surface p-6" data-testid="np-last-run">
          <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-3">// LAST RUN GENERATED</div>
          <div className="space-y-3">
            {lastResult.queued.map((q) => (
              <div key={q.queue_id} className="rounded-lg border border-line bg-bg-elevated p-3" data-testid={`last-q-${q.queue_id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="rounded-full bg-brand-amber/15 text-brand-amber px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider">{PLATFORM_LABEL[q.platform] || q.platform}</span>
                  <StatusBadge status={q.status} />
                </div>
                <p className="text-sm text-ink-primary whitespace-pre-wrap">{q.caption}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============== QUEUE (approval) ============== */
function QueueTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/nowplaying/queue");
      setItems(res.data.filter((i) => ["pending_approval", "held"].includes(i.status)));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      const res = await api.post(`/nowplaying/queue/${id}/approve`);
      const updated = res.data;
      if (updated.status === "held") {
        toast.success(`Held for peak hours — releases ${new Date(updated.release_at).toLocaleString()}`);
      } else {
        toast.success(updated.status === "posted" ? "Posted!" : updated.error || "Approved");
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const skip = async (id) => {
    await api.post(`/nowplaying/queue/${id}/skip`);
    setItems(items.filter((i) => i.queue_id !== id));
    toast.success("Skipped");
  };
  const save = async () => {
    try {
      await api.patch(`/nowplaying/queue/${editing}`, { caption: editText });
      toast.success("Caption updated");
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="np-queue">
      {loading ? (
        <div className="text-center py-12 font-mono text-xs text-ink-muted">LOADING QUEUE...</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center text-ink-muted text-sm" data-testid="np-queue-empty">
          <CheckCircle2 className="h-8 w-8 mx-auto mb-3 opacity-50" />
          Nothing waiting for approval. Run the auto-poster from the Live tab.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((q) => {
            const cardUrl = `${API_BASE}/nowplaying/card.png?title=${encodeURIComponent(q.title || q.raw_track || "")}&artist=${encodeURIComponent(q.artist || "")}`;
            return (
            <div key={q.queue_id} className="rounded-xl border border-brand-amber/30 bg-bg-surface p-5" data-testid={`np-q-${q.queue_id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="rounded-full bg-brand-amber/15 text-brand-amber px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider">{PLATFORM_LABEL[q.platform] || q.platform}</span>
                    <StatusBadge status={q.status} />
                    <span className="font-mono text-[10px] text-ink-muted">{new Date(q.created_at).toLocaleString()}</span>
                    {q.status === "held" && q.release_at && (
                      <span className="font-mono text-[10px] text-violet-300" data-testid={`np-release-at-${q.queue_id}`}>
                        <Clock className="h-2.5 w-2.5 inline -mt-0.5" /> releases {new Date(q.release_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="type-list-title text-ink-primary">
                    {q.artist ? `${q.artist} — ${q.title}` : q.raw_track}
                  </div>
                </div>
                {/* Update #193 · branded share-image thumbnail, click to download */}
                <a
                  href={`${cardUrl}&download=1`}
                  title="Download the share image for this post"
                  data-testid={`np-card-thumb-${q.queue_id}`}
                  className="shrink-0 group relative"
                >
                  <img src={cardUrl} alt="" loading="lazy"
                       className="h-24 w-24 rounded-lg border border-line object-cover group-hover:border-brand-amber/60 transition" />
                  <span className="absolute inset-0 hidden group-hover:flex items-center justify-center rounded-lg bg-black/50">
                    <Download className="h-4 w-4 text-brand-amber" />
                  </span>
                </a>
              </div>
              {editing === q.queue_id ? (
                <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={4} className="bg-bg border-line" data-testid={`np-edit-${q.queue_id}`} />
              ) : (
                <>
                  <p className="type-body text-ink-secondary whitespace-pre-wrap" data-testid={`np-caption-${q.queue_id}`}>{q.caption}</p>
                  {q.imaging_cart && (
                    <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-brand-onair/30 bg-brand-onair/5 px-2 py-1 text-[10px] font-mono">
                      <span className="text-brand-onair">CART · {q.imaging_cart.cart_id}</span>
                      <span className="text-ink-muted">{q.imaging_cart.title}</span>
                    </div>
                  )}
                </>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                {editing === q.queue_id ? (
                  <>
                    <button onClick={save} data-testid={`np-save-${q.queue_id}`} className="inline-flex items-center gap-1 rounded-md bg-brand-amber px-3 py-1.5 type-button-label text-black amber-glow">
                      <Save className="h-3 w-3" /> Save
                    </button>
                    <button onClick={() => setEditing(null)} className="rounded-md border border-line bg-bg-elevated px-3 py-1.5 type-button-label">Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => approve(q.queue_id)} data-testid={`np-approve-${q.queue_id}`} className="inline-flex items-center gap-1 rounded-md bg-brand-amber px-3 py-1.5 type-button-label text-black amber-glow">
                      <CheckCircle2 className="h-3 w-3" /> {q.status === "held" ? "Post now" : "Approve & post"}
                    </button>
                    <button onClick={() => { setEditing(q.queue_id); setEditText(q.caption); }} data-testid={`np-edit-btn-${q.queue_id}`} className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-1.5 type-button-label">
                      <Edit3 className="h-3 w-3" /> Edit
                    </button>
                    <button onClick={() => skip(q.queue_id)} data-testid={`np-skip-${q.queue_id}`} className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-3 py-1.5 type-button-label hover:text-brand-onair transition">
                      <X className="h-3 w-3" /> Skip
                    </button>
                  </>
                )}
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

/* ============== HISTORY ============== */
function HistoryTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const res = await api.get(`/nowplaying/queue${q}`);
      setItems(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div data-testid="np-history">
      <div className="mb-4 flex gap-2 flex-wrap">
        {["all", "posted", "failed", "pending_keys", "skipped"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`history-filter-${s}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
              filter === s ? "border-brand-amber bg-brand-amber/15 text-brand-amber" : "border-line bg-bg-surface text-ink-secondary"
            }`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="text-center py-12 font-mono text-xs text-ink-muted">LOADING...</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center text-ink-muted text-sm">
          <Clock className="h-8 w-8 mx-auto mb-3 opacity-50" />
          No posts yet.
        </div>
      ) : (
        <div className="rounded-xl border border-line bg-bg-surface overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-line text-left">
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-muted">When</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Platform</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Track</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Status</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Caption</th>
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.queue_id} className="border-b border-line/60 hover:bg-bg-elevated transition" data-testid={`history-row-${q.queue_id}`}>
                  <td className="px-4 py-3 font-mono text-[10px] text-ink-muted whitespace-nowrap">{new Date(q.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm">{PLATFORM_LABEL[q.platform] || q.platform}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="line-clamp-1">{q.artist ? `${q.artist} — ${q.title}` : q.raw_track}</div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                  <td className="px-4 py-3 text-xs text-ink-secondary max-w-md">
                    <div className="line-clamp-2">{q.caption}</div>
                    {q.error && <div className="text-brand-onair font-mono text-[10px] mt-1">{q.error}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============== RULES / SETUP ============== */
function RulesTab() {
  const [cfg, setCfg] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [station, setStation] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [c, p, s] = await Promise.all([
      api.get("/nowplaying/config"),
      api.get("/nowplaying/platforms"),
      api.get("/stations/me"),
    ]);
    setCfg(c.data);
    setPlatforms(p.data);
    setStation(s.data);
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="text-center py-12 font-mono text-xs text-ink-muted">LOADING...</div>;

  const update = (k, v) => setCfg({ ...cfg, [k]: v });
  const togglePlatform = (p) => {
    const next = cfg.platforms.includes(p) ? cfg.platforms.filter((x) => x !== p) : [...cfg.platforms, p];
    update("platforms", next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        stream_url: cfg.stream_url,
        silence_detection_enabled: !!cfg.silence_detection_enabled,
        platforms: cfg.platforms,
        frequency: cfg.frequency,
        require_approval: cfg.require_approval,
        avoid_duplicate_artist: cfg.avoid_duplicate_artist,
        duplicate_window_hours: Number(cfg.duplicate_window_hours) || 4,
        filter_words: typeof cfg.filter_words === "string" ? cfg.filter_words.split(",").map((s) => s.trim()).filter(Boolean) : cfg.filter_words,
        include_listen_live_link: cfg.include_listen_live_link,
        listen_live_url: cfg.listen_live_url,
        affiliate_template: cfg.affiliate_template,
        affiliate_enabled: cfg.affiliate_enabled,
        show_restrictions: cfg.show_restrictions || [],
        auto_cover_art: cfg.auto_cover_art,
        attach_imaging_cart: !!cfg.attach_imaging_cart,
        cart_pool_types: cfg.cart_pool_types || [],
        best_time_enabled: !!cfg.best_time_enabled,
        best_time_timezone: cfg.best_time_timezone || "America/New_York",
        best_time_windows: cfg.best_time_windows || [],
      };
      await api.patch("/nowplaying/config", payload);
      toast.success("Settings saved");
      load();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="np-rules">
      <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="type-section-heading text-ink-primary">Auto-poster engine</h3>
            <div className="type-list-subtitle text-ink-muted">Turn on to start polling and queueing posts.</div>
          </div>
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => update("enabled", v)} data-testid="np-enabled" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Stream URL (Shoutcast/Icecast/SAM)</Label>
          <Input value={cfg.stream_url || ""} onChange={(e) => update("stream_url", e.target.value)} placeholder="https://example.com:8000/stream" className="mt-1 bg-bg border-line font-mono text-xs" data-testid="np-stream-url" />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-line bg-bg p-3">
          <div className="pr-4">
            <div className="text-xs font-semibold text-ink-primary">Dead-air detection</div>
            <div className="text-[11px] text-ink-muted mt-0.5">Every 5 minutes we sample a few seconds of your stream — bell + email alert the moment it goes silent, and another when audio returns.</div>
          </div>
          <Switch checked={!!cfg.silence_detection_enabled} onCheckedChange={(v) => update("silence_detection_enabled", v)} data-testid="np-silence-toggle" />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Listen live URL (added to posts)</Label>
            <Input value={cfg.listen_live_url || ""} onChange={(e) => update("listen_live_url", e.target.value)} className="mt-1 bg-bg border-line font-mono text-xs" data-testid="np-listen-url" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Posting frequency</Label>
            <Select value={cfg.frequency} onValueChange={(v) => update("frequency", v)}>
              <SelectTrigger className="mt-1 bg-bg border-line" data-testid="np-frequency"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-bg-elevated border-line">
                <SelectItem value="every">Every song</SelectItem>
                <SelectItem value="every_3">Every 3 songs</SelectItem>
                <SelectItem value="every_5">Every 5 songs</SelectItem>
                <SelectItem value="every_10">Every 10 songs</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-5">
        <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// PLATFORMS</div>
        <p className="text-xs text-ink-secondary">Select where Now Playing posts go. Platforms not yet connected will show <span className="font-mono text-brand-amber">PENDING_KEYS</span> in the queue — connect them in <Link to="/dashboard/integrations" className="text-brand-amber hover:underline">Integrations</Link>.</p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
          {platforms.map((p) => (
            <label key={p.platform} className={`flex items-center justify-between rounded-lg border px-3 py-2.5 cursor-pointer transition ${cfg.platforms.includes(p.platform) ? "border-brand-amber/60 bg-brand-amber/5" : "border-line bg-bg-elevated"}`} data-testid={`np-platform-${p.platform}`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <PlatformIcon platform={p.platform} className="h-4 w-4 shrink-0" style={{ color: getPlatformMeta(p.platform)?.color }} />
                <div className="min-w-0">
                  <div className="type-list-title text-ink-primary">{p.label}</div>
                  <div className="font-mono type-status-badge text-ink-muted">{p.status.replace("_", " ")}</div>
                </div>
              </div>
              <input type="checkbox" checked={cfg.platforms.includes(p.platform)} onChange={() => togglePlatform(p.platform)} className="accent-[#0093FF] h-4 w-4" />
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-5">
        <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// RULES</div>
        <ToggleRow label="Require manual approval" hint="Every queued post lands in the Approval tab first." checked={!!cfg.require_approval} onChange={(v) => update("require_approval", v)} testid="np-rule-approval" />
        <ToggleRow label="Avoid duplicate artists" hint="Suppress posts when the same artist played recently." checked={!!cfg.avoid_duplicate_artist} onChange={(v) => update("avoid_duplicate_artist", v)} testid="np-rule-dupe" />
        {cfg.avoid_duplicate_artist && (
          <div className="ml-1">
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Dedupe window (hours)</Label>
            <Input type="number" min={1} max={48} value={cfg.duplicate_window_hours || 4} onChange={(e) => update("duplicate_window_hours", e.target.value)} className="mt-1 bg-bg border-line w-32" data-testid="np-rule-window" />
          </div>
        )}
        <div>
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Filter words (comma-separated)</Label>
          <Input
            value={Array.isArray(cfg.filter_words) ? cfg.filter_words.join(", ") : (cfg.filter_words || "")}
            onChange={(e) => update("filter_words", e.target.value)}
            className="mt-1 bg-bg border-line font-mono text-xs"
            placeholder="jingle, commercial, promo, station id"
            data-testid="np-rule-filter"
          />
          <div className="text-[10px] text-ink-muted mt-1 font-mono">Tracks containing these words are skipped (e.g. station IDs, jingles, ads).</div>
        </div>
        <ToggleRow label="Append listen-live link" hint="Adds the URL above to every generated post." checked={!!cfg.include_listen_live_link} onChange={(v) => update("include_listen_live_link", v)} testid="np-rule-listen" />
        <ToggleRow label="Append affiliate music link" hint="e.g. Spotify search link for monetization." checked={!!cfg.affiliate_enabled} onChange={(v) => update("affiliate_enabled", v)} testid="np-rule-affiliate" />
        {cfg.affiliate_enabled && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary">Affiliate template ({"{artist}"} & {"{title}"} placeholders)</Label>
            <Input value={cfg.affiliate_template || ""} onChange={(e) => update("affiliate_template", e.target.value)} className="mt-1 bg-bg border-line font-mono text-xs" data-testid="np-rule-template" />
          </div>
        )}
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-5">
        <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// BEST TIME POSTING</div>
        <ToggleRow label="Hold posts for peak listener hours" hint="Posts created outside the windows below wait and release automatically at the next one. 'Post now' on a held item overrides it." checked={!!cfg.best_time_enabled} onChange={(v) => update("best_time_enabled", v)} testid="np-best-time-toggle" />
        {cfg.best_time_enabled && (
          <>
            <div className="w-full sm:w-72">
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Station timezone</Label>
              <Select value={cfg.best_time_timezone || "America/New_York"} onValueChange={(v) => update("best_time_timezone", v)}>
                <SelectTrigger className="mt-1 bg-bg border-line" data-testid="np-best-time-tz"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-bg-elevated border-line">
                  {TIMEZONES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Peak windows</Label>
              {(cfg.best_time_windows || []).map((w, i) => (
                <BestTimeWindowRow key={i} w={w} idx={i}
                  onChange={(patch) => {
                    const next = [...(cfg.best_time_windows || [])];
                    next[i] = { ...next[i], ...patch };
                    update("best_time_windows", next);
                  }}
                  onRemove={() => update("best_time_windows", (cfg.best_time_windows || []).filter((_, j) => j !== i))}
                />
              ))}
              {(cfg.best_time_windows || []).length < 8 && (
                <button onClick={() => update("best_time_windows", [...(cfg.best_time_windows || []), { label: "New window", days: ["mon", "tue", "wed", "thu", "fri"], start: "12:00", end: "14:00" }])}
                  data-testid="np-best-time-add-window"
                  className="rounded-md border border-dashed border-line px-3 py-1.5 text-[11px] text-ink-muted hover:text-brand-amber hover:border-brand-amber/50 transition">
                  + Add window
                </button>
              )}
            </div>
            <BestTimeInsights />
          </>
        )}
      </div>

      <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-5">
        <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// IMAGING STUDIO → NOW PLAYING (auto-pipeline)</div>
        <p className="text-xs text-ink-secondary">Attach a randomly-picked imaging cart (Station ID, Sweeper, Jingle…) to each queued post. Carts come from the <Link to="/dashboard/imaging" className="text-brand-amber hover:underline">Imaging Studio Library</Link>.</p>
        <ToggleRow label="Attach imaging cart to each post" hint="Pulls one cart per post from the pool below." checked={!!cfg.attach_imaging_cart} onChange={(v) => update("attach_imaging_cart", v)} testid="np-rule-cart-attach" />
        {cfg.attach_imaging_cart && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-ink-secondary mb-2 block">Cart pool (which imaging types to draw from)</Label>
            <div className="flex flex-wrap gap-2">
              {["station_id", "sweeper", "jingle", "promo", "dj_drop", "show_opener", "podcast_intro", "sponsor_tag"].map((t) => {
                const active = (cfg.cart_pool_types || []).includes(t);
                return (
                  <button
                    key={t}
                    onClick={() => {
                      const cur = cfg.cart_pool_types || [];
                      const next = active ? cur.filter((x) => x !== t) : [...cur, t];
                      update("cart_pool_types", next);
                    }}
                    data-testid={`np-cart-pool-${t}`}
                    className={`rounded-full border px-3 py-1 text-[11px] transition ${active ? "border-brand-amber bg-brand-amber/15 text-brand-amber" : "border-line bg-bg-elevated text-ink-secondary"}`}
                  >
                    {t.replace(/_/g, " ")}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-ink-muted mt-2 font-mono">Empty pool → no cart attached. Add new pieces from the Imaging Studio anytime.</div>
          </div>
        )}
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="np-save-btn" className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-6 py-2.5 type-button-label text-black amber-glow disabled:opacity-50 shadow-lg">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving..." : "Save settings"}
        </button>
      </div>
    </div>
  );
}

/* ============== bits ============== */
function StatusBadge({ status }) {
  const v = STATUS_PILL[status] || STATUS_PILL.skipped;
  return <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${v.cls}`}>{v.label}</span>;
}

function Row({ label, value, accent, mono }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{label}</span>
      <span className={`text-xs ${mono ? "font-mono" : ""} ${accent ? "text-brand-amber font-medium" : "text-ink-primary"}`}>{value}</span>
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-line bg-bg-elevated p-3">
      <div>
        <div className="type-list-title text-ink-primary">{label}</div>
        {hint && <div className="type-list-subtitle text-ink-muted">{hint}</div>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern (New York)" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska" },
  { value: "Pacific/Honolulu", label: "Hawaii" },
  { value: "Europe/London", label: "UK (London)" },
  { value: "Europe/Berlin", label: "Central Europe (Berlin)" },
  { value: "Europe/Paris", label: "France (Paris)" },
  { value: "Australia/Sydney", label: "Australia (Sydney)" },
  { value: "UTC", label: "UTC" },
];

const BT_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function BestTimeInsights() {
  const [peak, setPeak] = useState(null);
  useEffect(() => {
    api.get("/analytics/peak-hours").then((r) => setPeak(r.data)).catch(() => {});
  }, []);
  if (!peak || (peak.peak.total + peak.off_peak.total) === 0) return null;
  return (
    <div className="rounded-lg border border-violet-500/25 bg-violet-500/5 px-3 py-2.5 text-[11px] text-ink-secondary flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="np-best-time-insights">
      <span className="font-mono text-[9px] uppercase tracking-wider text-violet-300">Last {peak.days} days</span>
      <span><strong className="text-violet-300">{peak.peak.total}</strong> released at peak</span>
      <span><strong className="text-ink-primary">{peak.off_peak.total}</strong> off-peak</span>
      {peak.peak.success_rate != null && <span>{peak.peak.success_rate}% peak success</span>}
      {peak.busiest_window && <span>busiest: <strong className="text-violet-300">{peak.busiest_window}</strong></span>}
      <Link to="/dashboard/analytics" className="ml-auto text-violet-300 hover:underline" data-testid="np-best-time-full-report">Full report →</Link>
    </div>
  );
}

function BestTimeWindowRow({ w, idx, onChange, onRemove }) {
  const toggleDay = (d) => {
    const days = w.days || [];
    onChange({ days: days.includes(d) ? days.filter((x) => x !== d) : [...days, d] });
  };
  return (
    <div className="rounded-lg border border-line bg-bg-elevated p-3 flex flex-wrap items-center gap-3" data-testid={`np-bt-window-${idx}`}>
      <Input value={w.label || ""} onChange={(e) => onChange({ label: e.target.value })}
        className="bg-bg border-line h-8 text-xs w-36" placeholder="Window name" data-testid={`np-bt-label-${idx}`} />
      <div className="flex gap-1">
        {BT_DAYS.map((d) => (
          <button key={d} onClick={() => toggleDay(d)} data-testid={`np-bt-day-${idx}-${d}`}
            className={`rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase transition ${(w.days || []).includes(d) ? "border-brand-amber bg-brand-amber/15 text-brand-amber" : "border-line text-ink-muted"}`}>
            {d}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1.5 text-[11px] text-ink-muted">
        <input type="time" value={w.start || "07:00"} onChange={(e) => onChange({ start: e.target.value })}
          className="rounded-md border border-line bg-bg px-2 py-1 text-xs text-ink-primary" data-testid={`np-bt-start-${idx}`} />
        →
        <input type="time" value={w.end || "09:00"} onChange={(e) => onChange({ end: e.target.value })}
          className="rounded-md border border-line bg-bg px-2 py-1 text-xs text-ink-primary" data-testid={`np-bt-end-${idx}`} />
      </div>
      <button onClick={onRemove} data-testid={`np-bt-remove-${idx}`}
        className="ml-auto text-ink-muted hover:text-brand-onair transition"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}
